import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './News.css';

const News = () => {
  const [newsList, setNewsList] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hexagons, setHexagons] = useState([]);
  const newsRef = useRef(null);
  const titleRef = useRef(null);
  const modalContentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (newsRef.current) observer.observe(newsRef.current);
    if (titleRef.current) observer.observe(titleRef.current);

    return () => {
      if (newsRef.current) observer.unobserve(newsRef.current);
      if (titleRef.current) observer.unobserve(titleRef.current);
    };
  }, []);

  useEffect(() => {
    fetch('/news-json/news-core.json') 
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        const basicNewsList = data.map(item => ({
          id: item.id || data.indexOf(item),
          date: item.date,
          header: item.header,
          link: item.link
        }));
        
        const sortedData = basicNewsList.sort((a, b) => new Date(b.date) - new Date(a.date));
        setNewsList(sortedData.slice(0, 5));
      })
      .catch(error => console.error('Error fetching news:', error));
  }, []);

  useEffect(() => {
    DOMPurify.addHook('afterSanitizeAttributes', function(node) {
      if ('target' in node) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
      
      if (node.nodeName === 'IFRAME') {
        node.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
      }
      
      if (node.hasAttribute('style')) {
        const allowedStyles = ['color', 'font-weight', 'font-style', 'text-decoration', 'text-align', 'margin', 'padding'];
        const style = node.getAttribute('style');
        const sanitizedStyle = style.split(';')
          .filter(rule => {
            const property = rule.split(':')[0]?.trim();
            return property && allowedStyles.includes(property);
          })
          .join(';');
        
        node.setAttribute('style', sanitizedStyle);
      }
    });
    
    DOMPurify.addHook('afterSanitizeAttributes', function(node) {
      if (node.hasAttribute('href') || node.hasAttribute('src')) {
        const urlAttrs = ['href', 'src'];
        urlAttrs.forEach(attr => {
          if (node.hasAttribute(attr)) {
            const value = node.getAttribute(attr);
            if (!(/^(?:https?:|mailto:|\/|\.\/|#)/.test(value))) {
              node.removeAttribute(attr);
            }
          }
        });
      }
    });
  }, []);

  const fetchArticleContent = (articleId) => {
    setIsLoading(true);
    
    fetch('/news-json/news-core.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        const fullArticle = data.find((item, index) => 
          (item.id && item.id === articleId) || (!item.id && index === articleId)
        );
        
        if (fullArticle) {
          setSelectedArticle(fullArticle);
        } else {
          throw new Error('Article not found');
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching article content:', error);
        setIsLoading(false);
      });
  };

  const openArticleModal = (articleId, event) => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
    fetchArticleContent(articleId);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
    document.body.style.overflow = 'auto';
  };

  const generateHexagons = useCallback((contentActualHeight, contentActualWidth) => {
    const hexagonCount = 25;

    const colors = [
      'rgba(82, 226, 183, 0.2)',
      'rgba(82, 226, 183, 0.15)',
      'rgba(82, 226, 183, 0.1)',
      'rgba(209, 219, 74, 0.15)',
      'rgba(0, 61, 45, 0.1)',
      'rgba(0, 179, 185, 0.15)',
      'rgba(81, 112, 19, 0.1)',
      'rgba(86, 198, 72, 0.1)',
      'rgba(249, 239, 217, 0.3)'
    ];

    const newHexagons = [];

    for (let i = 0; i < hexagonCount; i++) {
      const size = Math.floor(Math.random() * 110) + 60;
      const topInPixels = Math.random() * Math.max(0, contentActualHeight - size);
      const leftPercentage = Math.random() > 0.5
        ? Math.random() * 90
        : Math.random() * -30 - 10;
      const rightPercentage = Math.random() > 0.7
        ? Math.random() * -30 - 10
        : null;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const rotation = Math.random() * 30 - 15;

      newHexagons.push({
        id: i,
        width: size,
        height: size,
        top: `${topInPixels}px`,
        left: rightPercentage === null ? `${leftPercentage}%` : null,
        right: rightPercentage !== null ? `${rightPercentage}%` : null,
        backgroundColor: color,
        transform: `rotate(${rotation}deg)`
      });
    }

    setHexagons(newHexagons);
  }, []);

  useEffect(() => {
    const updateHexagonsLayout = () => {
      if (modalContentRef.current) {
        const currentModalContent = modalContentRef.current;
        const scrollHeight = currentModalContent.scrollHeight;
        const clientWidth = currentModalContent.clientWidth;

        const hexagonContainer = currentModalContent.querySelector('.modal-hexagon-container');
        if (hexagonContainer) {
          hexagonContainer.style.height = `${scrollHeight}px`;
        }
        generateHexagons(scrollHeight, clientWidth);
      }
    };

    if (isModalOpen && selectedArticle) {
      const timer = setTimeout(updateHexagonsLayout, 100);
      
      window.addEventListener('resize', updateHexagonsLayout);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateHexagonsLayout);
        if (modalContentRef.current) {
          const hexagonContainer = modalContentRef.current.querySelector('.modal-hexagon-container');
          if (hexagonContainer) {
            hexagonContainer.style.height = '100%';
          }
        }
      };
    } else {
      setHexagons([]);
      if (modalContentRef.current) {
        const hexagonContainer = modalContentRef.current.querySelector('.modal-hexagon-container');
        if (hexagonContainer) {
          hexagonContainer.style.height = '100%';
        }
      }
    }
  }, [isModalOpen, selectedArticle, generateHexagons]);

  return (
    <div className="news-section">
      <h2 className="news-title" ref={titleRef}>Latest Updates</h2>
      <div className="news-container" ref={newsRef}>
        <table className="news-table">
          <tbody>
            {newsList.map((newsItem, index) => (
              <tr key={index}>
                <td>{DOMPurify.sanitize(newsItem.date)}</td>
                <td>
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      openArticleModal(newsItem.id !== undefined ? newsItem.id : index, e);
                    }}
                    className="news-link"
                  >
                    {DOMPurify.sanitize(newsItem.header)}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="article-modal-overlay" onClick={closeModal}>
          <div 
            className="article-modal-content" 
            onClick={(e) => e.stopPropagation()}
            ref={modalContentRef}
          >
            <button className="modal-close-btn" onClick={closeModal}>×</button>
            
            <div className="modal-hexagon-container">
              {hexagons.map(hexagon => (
                <div 
                  key={hexagon.id}
                  className="modal-hexagon"
                  style={{
                    width: `${hexagon.width}px`,
                    height: `${hexagon.height}px`,
                    top: hexagon.top,
                    left: hexagon.left,
                    right: hexagon.right,
                    backgroundColor: hexagon.backgroundColor,
                    transform: hexagon.transform
                  }}
                />
              ))}
            </div>
            
            {isLoading ? (
              <div className="article-loading">
                <div className="loader"></div>
                <p>Loading article...</p>
              </div>
            ) : selectedArticle ? (
              <>
                <h2 className="article-heading">{DOMPurify.sanitize(selectedArticle.header)}</h2>
                <p className="article-date">{DOMPurify.sanitize(selectedArticle.date)}</p>
                <img 
                  src={DOMPurify.sanitize(selectedArticle.image)} 
                  alt="Article" 
                  className="article-image" 
                />
                <div 
                  className="article-content" 
                  style={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(selectedArticle.content, {
                      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'a', 'p', 'br', 'img', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'],
                      ALLOWED_ATTR: ['href', 'src', 'alt', 'width', 'height', 'style', 'target', 'rel'],
                      ADD_TAGS: ['iframe'],
                      ADD_ATTR: ['frameborder', 'allow', 'allowfullscreen', 'sandbox'],
                      FORBID_TAGS: ['script', 'object', 'embed'],
                      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
                      FORCE_HTTPS: true,
                      USE_PROFILES: { html: true }
                    })
                  }}
                />
                <div className="article-author">
                  <p>Written by: {DOMPurify.sanitize(selectedArticle.author)}</p>
                </div>
              </>
            ) : (
              <div className="article-error">
                <p>Sorry, we couldn't load this article.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default News;